import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import JsonLd from '../../components/base/JsonLd';

export default function TechnologyPage() {
  const [isNavFixed, setIsNavFixed] = useState(false);
  const [activeLayer, setActiveLayer] = useState(0);
  const [activeProcess, setActiveProcess] = useState(0);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showVideoModal, setShowVideoModal] = useState(false);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://donghua-painting.com';

  const techJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${siteUrl}/technology`,
    url: `${siteUrl}/technology`,
    name: '專業工藝 | 三層防護塗裝系統 | 東樺建築塗裝',
    description: '東樺建築塗裝採用德日進口塗料，以底塗防水、中塗彈性、面塗抗UV三層防護系統，提供最完整的建築外牆保護工藝。',
    inLanguage: 'zh-TW',
    isPartOf: { '@id': `${siteUrl}/#website` },
    about: [
      {
        '@type': 'Service',
        name: '建築外牆塗裝系統',
        provider: { '@id': `${siteUrl}/#business` },
        description: '採用底塗防水、中塗彈性、面塗抗UV的三層防護系統，搭配德日進口高耐候塗料，為建築提供長效保護。',
        serviceType: '建築外牆塗裝',
        areaServed: '台灣',
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: '塗裝工藝服務',
          itemListElement: [
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '底塗層防水工程' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '中塗層彈性補強' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '面塗層抗UV塗裝' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '異材質介面接著' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '微整平塗料工程' } },
          ],
        },
      },
    ],
  };

  useEffect(() => {
    document.title = '專業工藝 | 三層防護塗裝系統 | 東樺建築塗裝';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', '東樺建築塗裝採用德日進口塗料，以底塗防水、中塗彈性、面塗抗UV三層防護系統，提供最完整的建築外牆塗裝保護工藝。20年技術積累，科學化施工流程。');
    }
    return () => {
      document.title = '東樺建築塗裝 | 台灣專業外牆塗裝 | 築於信任繪於精緻';
      if (metaDesc) {
        metaDesc.setAttribute('content', '東樺建築塗裝成立於2005年,擁有20年專業經驗的台灣專業建築外牆塗裝專家。採用德日進口塗料,提供高品質的建築外牆塗裝服務。');
      }
    };
  }, []);

  const layers = [
    {
      title: '底塗層介面',
      subtitle: '深層防護系統',
      description: '深層滲透混凝土毛細孔,形成完整的接著屏障。有效阻隔水氣侵蝕,保護建築結構不受潮濕損害。',
      features: ['深層滲透技術', '完整防水屏障', '抗鹽害處理', '長效防護'],
      color: 'bg-[#34495E]',
      icon: 'ri-shield-check-line',
    },
    {
      title: '中塗層紋理',
      subtitle: '彈性強化系統',
      description: '採用多層交叉塗佈工法,增強塗層附著力與彈性。能有效抵抗建築物的微幅震動、溫度變化與結構應力,延長塗層使用壽命。',
      features: ['多層交叉塗佈', '彈性抗裂技術', '溫度適應性', '結構補強'],
      color: 'bg-[#7F8C8D]',
      icon: 'ri-hammer-line',
    },
    {
      title: '面層抗UV',
      subtitle: '耐候保護系統',
      description: '採用高耐候型矽酸樹脂塗料,具有優異的抗紫外線、耐酸鹼與耐污染能力。讓建築在烈日與風雨之中,依然保持亮麗如新的外觀。',
      features: ['抗紫外線', '耐酸鹼侵蝕', '防污自潔', '色彩持久'],
      color: 'bg-[#D4AF37]',
      icon: 'ri-sun-line',
    },
    {
      title: '異材質介面接著',
      subtitle: '翻新接著系統',
      description: '採用優質丙烯酸合成樹脂乳液精緻而成，能有效與磁磚表面達到最佳附著性，是舊建築物翻新表面的首選材料。',
      features: ['附著性佳', '耐水性優良', '封閉性高', '能與不同材質有效接著'],
      color: 'bg-[#34495E]',
      icon: 'ri-links-line',
    },
    {
      title: '微整平塗料',
      subtitle: '表面平整系統',
      description: '單組份系統，具有填充效果及強度高的粘結性，有效達到表面平整。',
      features: ['不易開裂', '附著性佳', '耐水性優良', '表面平整'],
      color: 'bg-[#7F8C8D]',
      icon: 'ri-equalizer-line',
    },
  ];

  const materials = [
    {
      name: '德國進口防水塗料',
      origin: '德國',
      features: '深層滲透、長效防水、環保無毒',
      certification: 'DIN 標準認證',
      image: 'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/e4de73a81060faf9f52632f8841b2407.jpeg',
    },
    {
      name: '日本矽酸樹脂塗料',
      origin: '日本',
      features: '高耐候、抗UV、色彩穩定',
      certification: 'JIS 工業標準',
      image: 'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/6f501f60542af535f120655b2e9c35a3.jpeg',
    },
    {
      name: '環保水性塗料',
      origin: '台灣',
      features: '低VOC、無異味、環境友善',
      certification: '綠建材標章',
      image: 'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/3cb18b1e8d7cf927d591cab1b80f6e36.jpeg',
    },
    {
      name: '彈性補強塗料',
      origin: '德國',
      features: '高彈性、抗裂、適應結構變化',
      certification: 'CE 認證',
      image: 'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/122b2c06c2f317a3d8dfecb944c3256b.jpeg',
    },
    {
      name: '奈米防污塗料',
      origin: '日本',
      features: '超疏水、自潔效果、抗菌防霉',
      certification: 'ISO 9001 認證',
      image: 'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/1563f9586dfa323d47fde58457c36404.jpeg',
    },
    {
      name: '隔熱反射塗料',
      origin: '美國',
      features: '高反射率、節能降溫、隔熱效果佳',
      certification: 'ENERGY STAR 認證',
      image: 'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/a311ebae642042e9563fef3001791cc4.jpeg',
    },
  ];

  const processes = [
    {
      step: '01',
      title: '現場勘查評估',
      description: '專業團隊到場勘查,評估建築現況、材質特性與施工環境,提供最適合的塗裝方案。',
      icon: 'ri-search-eye-line',
      image: 'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/94a183d0af2f364719a438be295e1742.jpeg',
    },
    {
      step: '02',
      title: '基層處理',
      description: '清除舊漆、修補裂縫、打磨平整,確保基層乾燥清潔,為後續施工打下良好基礎。',
      icon: 'ri-tools-line',
      image: 'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/6234e591e6be8f0d4d3963ffae2a3828.jpeg',
    },
    {
      step: '03',
      title: '底塗層介面施工',
      description: '深層滲透混凝土毛細孔,形成完整的接著屏障,有效阻隔水氣侵蝕。',
      icon: 'ri-drop-line',
      image: 'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/9c348ecc2eeba6715cb3147418f782bc.jpeg',
    },
    {
      step: '04',
      title: '中塗層紋理塗佈',
      description: '多層交叉塗佈,增強附著力與彈性,有效抵抗溫度變化與結構應力。',
      icon: 'ri-checkbox-multiple-line',
      image: 'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/37b870693eeb056f8f44671504e2755f.jpeg',
    },
    {
      step: '05',
      title: '面層精緻塗裝',
      description: '使用高耐候塗料,精準控制塗裝厚度與均勻度,確保色彩一致與長效保護。',
      icon: 'ri-paint-brush-line',
      image: 'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/44491e66091c4f82f881c6ecb063a276.jpeg',
    },
    {
      step: '06',
      title: '品質檢測驗收',
      description: '進行塗層厚度測量、附著力測試、外觀檢查,確保施工品質符合標準。',
      icon: 'ri-checkbox-circle-line',
      image: 'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/9aec95e3140ef5876c9ddb664303b9ae.jpeg',
    },
  ];

  const handleProcessHover = (index: number) => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    const timeout = setTimeout(() => {
      setActiveProcess(index);
    }, 500);
    setHoverTimeout(timeout);
  };

  const handleProcessClick = (index: number) => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    setActiveProcess(index);
  };

  const handleProcessLeave = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
  };

  const quality = [
    {
      title: '塗裝厚度檢測',
      description: '進行耐水性壓力測驗，兼具防水特性,確保每層塗料達到標準厚度,保證防護效果。',
      icon: 'ri-ruler-line',
      metric: '±1.8mm 厚度',
    },
    {
      title: '附著力測試',
      description: '採用拉拔試驗法,檢測塗層與基材的附著強度,確保不會剝落。',
      icon: 'ri-link-unlink-m',
      metric: '≥1.2 MPa',
    },
    {
      title: '防水性能測試',
      description: '進行淋水試驗與壓力測試,驗證防水系統的完整性與可靠性。',
      icon: 'ri-water-flash-line',
      metric: '480小時無滲漏',
    },
    {
      title: '環境溫濕度監控',
      description: '施工期間持續監測溫度與濕度,確保在最佳環境條件下施工。',
      icon: 'ri-temp-hot-line',
      metric: '15-35°C / 85%以下',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <JsonLd id="technology-jsonld" data={techJsonLd} />
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isNavFixed ? 'bg-white shadow-lg' : 'bg-white'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3">
            <img
              src="https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/d57786512cad84463bdc66c219dc39c4.png"
              alt="東樺建築塗裝"
              className="h-12"
            />
          </Link>
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className="text-sm font-medium text-gray-900 hover:text-[#D4AF37] transition-colors cursor-pointer"
            >
              首頁
            </Link>
            <Link
              to="/about"
              className="text-sm font-medium text-gray-900 hover:text-[#D4AF37] transition-colors cursor-pointer"
            >
              關於東樺
            </Link>
            <Link
              to="/technology"
              className="text-sm font-medium text-[#D4AF37] transition-colors cursor-pointer"
            >
              專業工藝
            </Link>
            <a
              href="#contact"
              className="bg-[#D4AF37] text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-[#B8941F] transition-colors whitespace-nowrap cursor-pointer"
            >
              聯絡我們
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#34495E] to-[#2C3E50]"></div>
        <div className="absolute inset-0 opacity-10">
          <div
            className="w-full h-full"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          ></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-[#D4AF37] text-sm font-medium mb-6">
              專業工藝
            </div>
            <h1 className="text-6xl md:text-7xl font-bold text-white mb-8 leading-tight">
              不只是表面的色彩
              <br />
              <span className="text-[#D4AF37]">更是結構與保護的技術結晶</span>
            </h1>
            <p className="text-xl text-white/80 leading-relaxed">
              以科學的態度與細膩的手工,結合進口塗料系統與二十年專業經驗,<br />
              為每一座建築打造最堅實的保護層。
            </p>
          </div>
        </div>
      </section>

      {/* Layer System */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">二加三層防護系統</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              從內到外的完整保護,確保建築外觀歷久彌新
            </p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6 mb-16">
            {layers.map((layer, index) => (
              <div
                key={index}
                onClick={() => setActiveLayer(index)}
                className={`${layer.color} rounded-2xl p-6 text-white cursor-pointer transition-all hover:shadow-2xl hover:-translate-y-2 ${
                  activeLayer === index ? 'shadow-2xl -translate-y-2' : 'shadow-lg'
                }`}
              >
                <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-4">
                  <i className={`${layer.icon} text-2xl`}></i>
                </div>
                <h3 className="text-lg font-bold mb-2">{layer.title}</h3>
                <div className="text-xs opacity-80 mb-3">{layer.subtitle}</div>
                <p className="mb-4 leading-relaxed opacity-90 text-sm">{layer.description}</p>
                <div className="space-y-1">
                  {layer.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <i className="ri-checkbox-circle-fill text-sm"></i>
                      <span className="text-xs">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-br from-[#F8F9FA] to-[#E8E9EB] rounded-2xl p-12">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  科學化的塗層結構
                </h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  每一層塗料都有其獨特的功能與作用。從最內層的防水保護,到中層的彈性補強,再到外層的耐候防護,形成完整的三重防護系統。
                </p>
                <p className="text-gray-600 leading-relaxed mb-6">
                  這種多層次的保護結構,不僅能有效延長建築外觀的使用壽命,更能在面對台灣潮濕多雨、日照強烈的氣候環境時,提供最可靠的保護。
                </p>
                <div className="inline-flex items-center text-[#D4AF37] font-medium hover:gap-3 gap-2 transition-all cursor-pointer">
                  了解更多技術細節
                  <i className="ri-arrow-right-line text-xl"></i>
                </div>
              </div>
              <div className="relative aspect-square w-full h-full">
                <img
                  src="https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/764f891bb241d15fa09fa7a7d781ca4f.png"
                  alt="塗層結構示意圖"
                  className="w-full h-full object-cover object-top rounded-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Materials */}
      <section className="py-24 bg-gradient-to-br from-[#F8F9FA] to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">嚴選塗料系統</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              堅持使用高品質進口高品質塗料,搭配環保水性系統,為建築提供最優質的保護
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {materials.map((material, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all"
              >
                <div className="aspect-[4/3] w-full h-full overflow-hidden relative">
                  <img
                    src={material.image}
                    alt={material.name}
                    className="w-full h-full object-cover object-top"
                  />
                  {index === 0 && (
                    <div className="absolute bottom-3 left-3 bg-black/90 text-white text-base font-mono px-5 py-2.5 rounded-lg tracking-widest font-bold shadow-xl border border-white/40">
                      IAS206-S09432
                    </div>
                  )}
                  {index === 1 && (
                    <div className="absolute bottom-3 left-3 bg-black/90 text-white text-base font-mono px-5 py-2.5 rounded-lg tracking-widest font-bold shadow-xl border border-white/40">
                      IAS301-S09179
                    </div>
                  )}
                  {index === 2 && (
                    <div className="absolute bottom-3 left-3 bg-black/90 text-white text-base font-mono px-5 py-2.5 rounded-lg tracking-widest font-bold shadow-xl border border-white/40">
                      IAS301-S10268
                    </div>
                  )}
                  {index === 3 && (
                    <div className="absolute bottom-3 left-3 bg-black/90 text-white text-base font-mono px-5 py-2.5 rounded-lg tracking-widest font-bold shadow-xl border border-white/40">
                      IAS301-S10266
                    </div>
                  )}
                  {index === 4 && (
                    <div className="absolute bottom-3 left-3 bg-black/90 text-white text-base font-mono px-5 py-2.5 rounded-lg tracking-widest font-bold shadow-xl border border-white/40">
                      IAS301-S11001
                    </div>
                  )}
                  {index === 5 && (
                    <div className="absolute bottom-3 left-3 bg-black/90 text-white text-base font-mono px-5 py-2.5 rounded-lg tracking-widest font-bold shadow-xl border border-white/40">
                      IAS301-S11616
                    </div>
                  )}
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <h3 className="text-2xl font-bold text-gray-900">{material.name}</h3>
                    <span className="px-3 py-1 bg-[#D4AF37]/10 text-[#D4AF37] text-sm font-medium rounded-full">
                      {material.origin}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-4 leading-relaxed">{material.features}</p>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <i className="ri-award-line text-[#D4AF37]"></i>
                    {material.certification}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">施工流程</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              標準化的施工流程,確保每個環節都達到最高品質要求
            </p>
          </div>

          <div className="relative h-[600px] rounded-3xl overflow-hidden shadow-2xl">
            {/* Background Image */}
            {processes.map((process, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-opacity duration-700 ${
                  activeProcess === index ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <img
                  src={process.image}
                  alt={process.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/30"></div>
              </div>
            ))}

            {/* Content */}
            <div className="relative h-full flex">
              {/* Left Sidebar - Process List */}
              <div className="w-80 h-full bg-black/40 backdrop-blur-sm p-6 flex flex-col justify-center">
                <div className="space-y-2">
                  {processes.map((process, index) => (
                    <div
                      key={index}
                      onMouseEnter={() => handleProcessHover(index)}
                      onMouseLeave={handleProcessLeave}
                      onClick={() => handleProcessClick(index)}
                      className={`group cursor-pointer transition-all duration-500 rounded-xl overflow-hidden ${
                        activeProcess === index
                          ? 'bg-[#D4AF37] p-5'
                          : 'bg-white/10 hover:bg-white/20 p-4'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div
                          className={`flex items-center justify-center transition-all duration-500 ${
                            activeProcess === index
                              ? 'w-14 h-14 bg-white/20 rounded-full'
                              : 'w-10 h-10 bg-white/10 rounded-full'
                          }`}
                        >
                          <span
                            className={`font-bold transition-all duration-500 ${
                              activeProcess === index
                                ? 'text-white text-xl'
                                : 'text-white text-sm'
                            }`}
                          >
                            {process.step}
                          </span>
                        </div>
                        <div className="flex-1">
                          <h3
                            className={`font-bold transition-all duration-500 ${
                              activeProcess === index
                                ? 'text-white text-lg'
                                : 'text-white text-sm'
                            }`}
                          >
                            {process.title}
                          </h3>
                        </div>
                        <i
                          className={`ri-arrow-right-s-line transition-all duration-500 ${
                            activeProcess === index
                              ? 'text-white text-2xl opacity-100'
                              : 'text-white text-lg opacity-0 group-hover:opacity-100'
                          }`}
                        ></i>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Content - Process Details */}
              <div className="flex-1 h-full flex items-center justify-center p-12">
                <div className="max-w-xl">
                  {processes.map((process, index) => (
                    <div
                      key={index}
                      className={`transition-all duration-700 ${
                        activeProcess === index
                          ? 'opacity-100 translate-y-0'
                          : 'opacity-0 translate-y-8 absolute pointer-events-none'
                      }`}
                    >
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-20 h-20 bg-[#D4AF37] rounded-full flex items-center justify-center shadow-2xl">
                          <i className={`${process.icon} text-4xl text-white`}></i>
                        </div>
                        <div className="text-7xl font-bold text-white/20">{process.step}</div>
                      </div>
                      <h3 className="text-4xl font-bold text-gray-900 mb-6">{process.title}</h3>
                      <p className="text-xl text-gray-600 leading-relaxed mb-8">
                        {process.description}
                      </p>
                      <div className="flex items-center gap-2 text-[#D4AF37]">
                        <i className="ri-checkbox-circle-fill text-xl"></i>
                        <span className="font-medium">專業標準施工</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Progress Indicator */}
            <div className="absolute bottom-6 right-6 flex items-center gap-2">
              {processes.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleProcessClick(index)}
                  className={`transition-all duration-300 rounded-full cursor-pointer ${
                    activeProcess === index
                      ? 'w-8 h-2 bg-[#D4AF37]'
                      : 'w-2 h-2 bg-white/40 hover:bg-white/60'
                  }`}
                  aria-label={`切換到步驟 ${index + 1}`}
                ></button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quality Control */}
      <section className="py-24 bg-[#34495E]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-6">品質檢測</h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto leading-relaxed">
              科學化的檢測流程,確保施工品質符合國際標準
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {quality.map((item, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all"
              >
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-3xl text-white`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-white/80 leading-relaxed mb-4">{item.description}</p>
                    <div className="inline-block px-4 py-2 bg-[#D4AF37] text-white rounded-full text-sm font-medium">
                      {item.metric}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-[#34495E]/10 rounded-full text-[#34495E] text-sm font-medium mb-6">
              施工實錄
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-6">工藝流程影片</h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              從基層處理到面層塗裝，完整呈現東樺的專業施工過程
            </p>
          </div>
          <div className="aspect-video bg-[#34495E]/10 rounded-2xl flex items-center justify-center border-2 border-[#34495E]/20 overflow-hidden relative group cursor-pointer" onClick={() => setShowVideoModal(true)}>
            <img
              src="https://public.readdy.ai/ai/img_res/e9478913d61afcadd0d6852e08e153d1.jpg"
              alt="施工工藝影片"
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="w-20 h-20 bg-[#D4AF37] rounded-full flex items-center justify-center mb-4 shadow-2xl group-hover:scale-110 transition-transform">
                <i className="ri-play-fill text-4xl text-white ml-1"></i>
              </div>
              <p className="text-white font-medium text-lg">觀看施工工藝影片</p>
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {showVideoModal && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 animate-fadeIn"
          onClick={() => setShowVideoModal(false)}
        >
          <button
            onClick={() => setShowVideoModal(false)}
            className="absolute top-6 right-6 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition-all cursor-pointer z-10"
          >
            <i className="ri-close-line text-white text-2xl"></i>
          </button>
          <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-8">
              <h3 className="text-4xl font-bold text-white mb-4">東樺建築塗裝 · 施工工藝實錄</h3>
              <p className="text-white/60 text-lg">科學化施工流程，確保每道工序達到最高品質標準</p>
            </div>
            <div className="aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/nKJwzpE8o6w"
                title="東樺施工工藝影片"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="text-center mt-8">
              <p className="text-white/60 text-sm">點擊任意處關閉</p>
            </div>
          </div>
        </div>
      )}

      {/* Technology Highlights */}
      <section className="py-24 bg-gradient-to-br from-[#F8F9FA] to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative aspect-[4/3] w-full h-full">
              <img
                src="https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/22ac238a11241c4e7ff89e91adf1abbb.jpeg"
                alt="品質檢測"
                className="w-full h-full object-cover object-top rounded-2xl shadow-2xl"
              />
            </div>

            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-8">
                精準控制的施工品質
              </h2>
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <div className="w-8 h-8 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
                      <i className="ri-microscope-line text-[#D4AF37]"></i>
                    </div>
                    科學化檢測
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    使用專業儀器進行塗層厚度、附著力、防水性能等多項檢測,確保毎個環節都符合標準。
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <div className="w-8 h-8 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
                      <i className="ri-temp-hot-line text-[#D4AF37]"></i>
                    </div>
                    環境監控
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    施工期間持續監測溫度與濕度,確保在最佳環境條件下施工,避免氣候因素影響品質
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <div className="w-8 h-8 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
                      <i className="ri-file-list-3-line text-[#D4AF37]"></i>
                    </div>
                    完整記錄
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    建立完整的施工記錄與檢測報告,讓每個工程都有可追溯的品質證明。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-bold text-gray-900 mb-8">
            讓專業工藝守護您的建築
          </h2>
          <p className="text-xl text-gray-600 mb-12 leading-relaxed">
            無論是新建工程或是老屋翻新,我們都能為您提供最專業的塗裝解決方案
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="#contact"
              className="bg-[#D4AF37] text-white px-8 py-4 rounded-full text-base font-medium hover:bg-[#B8941F] transition-all cursor-pointer whitespace-nowrap"
            >
              聯絡我們
            </a>
            <Link
              to="/"
              className="border-2 border-[#34495E] text-[#34495E] px-8 py-4 rounded-full text-base font-medium hover:bg-[#34495E] hover:text-white transition-all cursor-pointer whitespace-nowrap"
            >
              查看工程實績
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16">
            <div>
              <div className="inline-block px-4 py-2 bg-[#D4AF37]/10 rounded-full text-[#D4AF37] text-sm font-medium mb-6">
                聯絡我們
              </div>
              <h2 className="text-5xl font-bold text-gray-900 mb-8 leading-tight">
                開始您的
                <br />
                建築美學之旅
              </h2>
              <p className="text-lg text-gray-600 mb-12 leading-relaxed">
                無論是新建工程或是老屋翻新,我們都能為您提供最專業的塗裝解決方案。
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4 text-gray-600">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-[#D4AF37]"></i>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">聯絡電話</div>
                    <div className="text-lg font-medium text-gray-900">03-3900-250</div>
                  </div>
                </div>

                <div className="flex items-start gap-4 text-gray-600">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl text-[#D4AF37]"></i>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">電子郵件</div>
                    <div className="text-lg font-medium text-gray-900">tunghua69566488@gmail.com</div>
                    <div className="text-lg font-medium text-gray-900">sales@tung-hua.com</div>
                  </div>
                </div>

                <div className="flex items-start gap-4 text-gray-600">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-[#D4AF37]"></i>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">公司地址</div>
                    <div className="text-lg font-medium text-gray-900">台灣 334桃園市八德區中正二街2號</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#F8F9FA] to-[#E8E9EB] rounded-2xl p-8">
              <form className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    姓名
                  </label>
                  <input
                    type="text"
                    name="name"
                    className="w-full px-4 py-3 border-b-2 border-[#D4AF37] bg-transparent focus:outline-none focus:border-[#B8941F] transition-colors"
                    placeholder="請輸入您的姓名"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    電子郵件
                  </label>
                  <input
                    type="email"
                    name="email"
                    className="w-full px-4 py-3 border-b-2 border-[#D4AF37] bg-transparent focus:outline-none focus:border-[#B8941F] transition-colors"
                    placeholder="請輸入您的電子郵件"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    聯絡電話
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    className="w-full px-4 py-3 border-b-2 border-[#D4AF37] bg-transparent focus:outline-none focus:border-[#B8941F] transition-colors"
                    placeholder="請輸入您的聯絡電話"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    諮詢內容
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border-b-2 border-[#D4AF37] bg-transparent focus:outline-none focus:border-[#B8941F] transition-colors resize-none"
                    placeholder="請描述您的需求..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-[#D4AF37] text-white py-4 rounded-full font-medium hover:bg-[#B8941F] transition-all cursor-pointer whitespace-nowrap"
                >
                  送出諮詢
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] text-white">
        <div className="py-16">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <h3 className="text-4xl font-bold text-[#D4AF37] mb-4 tracking-wider">
              專業塗裝 · 誠信永續 · 築出永恆的風景
            </h3>
          </div>
        </div>

        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-12">
            <div className="grid md:grid-cols-4 gap-12">
              <div>
                <img
                  src="https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/d57786512cad84463bdc66c219dc39c4.png"
                  alt="東樺建築塗裝"
                  className="h-12 mb-4"
                />
                <p className="text-white/60 text-sm leading-relaxed">
                  二十年專注建築外牆塗裝,以專業工藝守護每一座建築的生命。
                </p>
              </div>

              <div>
                <h4 className="text-lg font-bold mb-4">快速連結</h4>
                <ul className="space-y-2">
                  <li>
                    <Link
                      to="/"
                      className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm cursor-pointer"
                    >
                      首頁
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/about"
                      className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm cursor-pointer"
                    >
                      關於東樺
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/technology"
                      className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm cursor-pointer"
                    >
                      專業工藝
                    </Link>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-bold mb-4">聯絡資訊</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2 text-white/60">
                    <i className="ri-phone-line"></i>
                    03-3900-250
                  </li>
                  <li className="flex items-center gap-2 text-white/60">
                    <i className="ri-mail-line"></i>
                    tunghua69566488@gmail.com
                  </li>
                  <li className="flex items-center gap-2 text-white/60">
                    <i className="ri-mail-line"></i>
                    sales@tung-hua.com
                  </li>
                  <li className="flex items-center gap-2 text-white/60">
                    <i className="ri-map-pin-line"></i>
                    台灣 334桃園市八德區中正二街2號
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-bold mb-4">追蹤我們</h4>
                <div className="flex gap-3">
                  <a
                    href="#"
                    className="w-10 h-10 rounded-full border border-[#D4AF37] flex items-center justify-center hover:bg-[#D4AF37] transition-all cursor-pointer"
                  >
                    <i className="ri-facebook-fill"></i>
                  </a>
                  <a
                    href="#"
                    className="w-10 h-10 rounded-full border border-[#D4AF37] flex items-center justify-center hover:bg-[#D4AF37] transition-all cursor-pointer"
                  >
                    <i className="ri-line-fill"></i>
                  </a>
                  <a
                    href="#"
                    className="w-10 h-10 rounded-full border border-[#D4AF37] flex items-center justify-center hover:bg-[#D4AF37] transition-all cursor-pointer"
                  >
                    <i className="ri-instagram-line"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 bg-[#0D0D0D]">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-white/40">
              © 2024 東樺建築塗裝有限公司. All rights reserved.
            </div>
            <div className="flex items-center gap-4">
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-white/40 hover:text-[#D4AF37] transition-colors cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
